import { useCallback, useMemo } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Button, confirm, toast } from "@devhop/ui";
import { HelpCircle, Plus } from "lucide-react";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import type { MeetingListItem } from "@/modules/management/meeting-minutes/domain/types";
import {
  useMeetingsQuery,
  useDeleteMeetingMutation,
} from "../api/queries";
import type { MeetingSearchParams } from "../ui/MeetingFilter";
import { MeetingFilter } from "../ui/MeetingFilter";
import { MeetingTable } from "../ui/MeetingTable";
import {
  MeetingTour,
  meetingListTourSteps,
  useMeetingTour,
} from "../tour";

export function MeetingListPage() {
  const nav = useNavigate({ from: "/app/management/meetings" });
  const search = useSearch({
    from: "/app/management/meetings",
  }) as MeetingSearchParams & {
    offset?: number;
    limit?: number;
    sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  };

  const sortBy = search.sort?.[0]?.field ?? "meetingDate";
  const sortOrder = (search.sort?.[0]?.dir ?? "desc") as "asc" | "desc";

  const query = useMemo(
    () => ({
      offset: search.offset ?? 0,
      limit: search.limit ?? 20,
      sort: search.sort,
      dateFrom: search.dateFrom,
      dateTo: search.dateTo,
      search: search.search,
    }),
    [
      search.offset,
      search.limit,
      search.sort,
      search.dateFrom,
      search.dateTo,
      search.search,
    ],
  );

  const listQuery = useMeetingsQuery(query);
  const data = listQuery.data?.data ?? [];
  const meta = listQuery.data?.meta;
  const totalCount = meta?.total ?? 0;
  const limit = meta?.limit ?? 20;
  const offset = search.offset ?? 0;

  const canCreate = usePermissions().has(Permissions.mgmt_meeting.create);
  const canUpdate = usePermissions().has(Permissions.mgmt_meeting.update);
  const canDelete = usePermissions().has(Permissions.mgmt_meeting.delete);
  const tour = useMeetingTour("list");

  const deleteMutation = useDeleteMeetingMutation();

  const handlePaginationChange = (newOffset: number, newLimit: number) => {
    nav({
      search: {
        offset: newOffset,
        limit: newLimit,
        sort: search.sort,
        dateFrom: search.dateFrom,
        dateTo: search.dateTo,
        search: search.search,
      },
    });
  };

  const handleView = (row: MeetingListItem) => {
    nav({ to: "/app/management/meetings/$id", params: { id: row.id } });
  };

  const handleEdit = (row: MeetingListItem) => {
    nav({
      to: "/app/management/meetings/$id/edit",
      params: { id: row.id },
    });
  };

  const handlePdfPreview = (row: MeetingListItem) => {
    nav({
      to: "/app/management/meetings/$id/pdf-preview",
      params: { id: row.id },
    });
  };

  const handleViewActions = (row: MeetingListItem) => {
    nav({
      to: "/app/management/meetings/actions",
      search: {
        offset: 0,
        limit: 20,
        sort: undefined,
        dueDateFrom: undefined,
        dueDateTo: undefined,
        status: undefined,
        assigneeId: undefined,
        meetingId: row.id,
      },
    });
  };

  const handleDelete = useCallback(
    async (row: MeetingListItem) => {
      const ok = await confirm({
        title: "ລຶບບັນທຶກການປະຊຸມ?",
        description: `"${row.title}" ຈະຖືກລຶບອອກຖາວອນ. ການກະທຳນີ້ບໍ່ສາມາດກູ້ຄືນໄດ້.`,
        actionText: "ລຶບ",
        ActionProps: { variant: "destructive" },
      });
      if (!ok) return;
      deleteMutation.mutate(row.id, {
        onSuccess: () => toast.success("ລຶບບັນທຶກການປະຊຸມແລ້ວ"),
        onError: () => toast.error("ບໍ່ສາມາດລຶບໄດ້"),
      });
    },
    [deleteMutation],
  );

  const handleSortingChange = useCallback(
    (id: string, desc: boolean) => {
      nav({
        search: {
          offset: 0,
          limit: search.limit ?? 20,
          sort: [
            {
              field: id,
              dir: (desc ? "desc" : "asc") as "asc" | "desc",
            },
          ],
          dateFrom: search.dateFrom,
          dateTo: search.dateTo,
          search: search.search,
        },
      });
    },
    [
      nav,
      search.limit,
      search.dateFrom,
      search.dateTo,
      search.search,
    ],
  );

  const handleNewMeeting = () => {
    nav({ to: "/app/management/meetings/new" });
  };

  return (
    <>
      <Header />
      <Main>
        <MeetingTour
          steps={meetingListTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div
          className="mb-6 flex items-center justify-between gap-2"
          data-tourid="meeting-list-header"
        >
          <div className="flex items-center gap-2">
            <h1 className="font-semibold text-2xl">ບັນທຶກການປະຊຸມ</h1>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 rounded-full"
              onClick={tour.startTour}
              title="ເລີ່ມທົດສອບການນຳທາງ"
              aria-label="ເລີ່ມທົດສອບການນຳທາງ"
            >
              <HelpCircle className="size-5" />
            </Button>
          </div>
          {canCreate && (
            <Button variant="default" onClick={handleNewMeeting} className="gap-2">
              <Plus className="h-4 w-4" />
              ເພີ່ມບັນທຶກປະຊຸມ
            </Button>
          )}
        </div>

        <div
          className="mb-6 flex flex-col rounded-xl border bg-card pt-2"
          data-tourid="meeting-list-filter"
        >
          <MeetingFilter
            searchParams={{ ...search, limit: search.limit ?? 20 }}
            fromRoute="/app/management/meetings"
          />
          <div data-tourid="meeting-list-table">
            <MeetingTable
              data={data}
            isLoading={listQuery.isLoading}
            offset={offset}
            limit={limit}
            totalCount={totalCount}
            onPaginationChange={handlePaginationChange}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortingChange={handleSortingChange}
            onView={handleView}
            onEdit={canUpdate ? handleEdit : undefined}
            onPdfPreview={handlePdfPreview}
            onViewActions={handleViewActions}
            onDelete={canDelete || canUpdate ? handleDelete : undefined}
            />
          </div>
        </div>
      </Main>
    </>
  );
}
